// ============================================
// UI Store (Zustand)
// ============================================

import { create } from 'zustand';

type ProjectKey = 'hot_mobile' | 'adult_store';

interface UIState {
  // Sidebar
  sidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  
  // Active project filter
  activeProject: ProjectKey | 'all';
  setActiveProject: (project: ProjectKey | 'all') => void;
  
  // Selected lead for detail view
  selectedLeadId: string | null;
  setSelectedLeadId: (id: string | null) => void;
  
  // Selected conversation for WhatsApp cockpit
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;
  
  // Modal states
  modals: {
    orderPack: boolean;
    eligibility: boolean;
    confirmAction: boolean;
    revealData: boolean;
  };
  openModal: (modal: keyof UIState['modals']) => void;
  closeModal: (modal: keyof UIState['modals']) => void;
  
  // Toast notifications
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
}

let toastId = 0;

export const useUIStore = create<UIState>((set) => ({
  // Sidebar
  sidebarOpen: true,
  toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  
  // Active project
  activeProject: 'all',
  setActiveProject: (project) => set({ activeProject: project }),
  
  // Selected lead
  selectedLeadId: null,
  setSelectedLeadId: (id) => set({ selectedLeadId: id }),
  
  // Selected conversation
  selectedConversationId: null,
  setSelectedConversationId: (id) => set({ selectedConversationId: id }),
  
  // Modals
  modals: {
    orderPack: false,
    eligibility: false,
    confirmAction: false,
    revealData: false,
  },
  openModal: (modal) => set((state) => ({
    modals: { ...state.modals, [modal]: true },
  })),
  closeModal: (modal) => set((state) => ({
    modals: { ...state.modals, [modal]: false },
  })),
  
  // Toasts
  toasts: [],
  addToast: (toast) => {
    const id = `toast-${++toastId}`;
    set((state) => ({
      toasts: [...state.toasts, { ...toast, id }],
    }));
    
    // Auto remove after duration
    const duration = toast.duration ?? 5000;
    setTimeout(() => {
      set((state) => ({
        toasts: state.toasts.filter((t) => t.id !== id),
      }));
    }, duration);
  },
  removeToast: (id) => set((state) => ({
    toasts: state.toasts.filter((t) => t.id !== id),
  })),
}));

// Helper hook for toasts
export const useToast = () => {
  const addToast = useUIStore((state) => state.addToast);
  
  return {
    success: (title: string, message?: string) => addToast({ type: 'success', title, message }),
    error: (title: string, message?: string) => addToast({ type: 'error', title, message }),
    warning: (title: string, message?: string) => addToast({ type: 'warning', title, message }),
    info: (title: string, message?: string) => addToast({ type: 'info', title, message }),
  };
};
